import { Injectable } from '@angular/core';
import axios from 'axios';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { FiltersService } from './filters.service';
import { HttpClientService } from './http-client.service';
import { AdvancedOneCustomerFilters } from 'src/app/config/GlobalCacheLoader.service';
import { availableBrandGroups, TvdLocationAdvancedFilter, TvdProductAdvancedFilter } from 'src/app/features/total-value-delivery/types/tvd-types';
import { CurrencyUomService, UomOption } from './currency-uom.service';
import { MetricCostAssumptions } from './ebr-api-client/ebr-api-client.g';

@Injectable({
  providedIn: 'root',
})
export class TvdRequests {
  private ENDPOINT_BASE_PATH: string = '/tvd';
  private filters: ContractModel | undefined;
  private oneCustomerName: string = '';
  private advancedOneCustomerFilters : AdvancedOneCustomerFilters | undefined;

  constructor(private httpClientService: HttpClientService,
    private filtersService: FiltersService,
    private currencyUomService: CurrencyUomService
  ) {
    this.filtersService.selectedFiltersObservable.subscribe(f => this.filters = f);
    this.filtersService.oneCustomerNameObservable.subscribe(f => this.oneCustomerName = f);
    this.filtersService.oneCustomerFiltersObservable.subscribe(f => this.advancedOneCustomerFilters = f);
  }

  /**
   * Dynamically retrieves TVD metrics based on filter selections. Fetches from endpoint: api/v2/tvd/tvd-metrics
   * @returns Groups of TVD metrics.
   */
  async getTotalValueDelivered(costAssumptionsMetrics?: MetricCostAssumptions) {
    try {
      if (this.filters == undefined)
        throw new Error('filters are undefined');
      const body = {
        contractModel :this.filters,
        oneCustomerName: this.oneCustomerName,
        costAssumptionsMetrics: costAssumptionsMetrics,
        oneCustomerFilters: this.advancedOneCustomerFilters,
        currencyCode: this.currencyUomService.getCurrency(),
        unitOfMeasure: this.currencyUomService.getUom()
      };

      let url = `${this.ENDPOINT_BASE_PATH}/tvd-metrics/?region=${this.filters.regions}&countries=${this.filters.countries}&period=${this.filters.period}`;
      url = this.filtersService.appendCustomDateRangeToUrl(url);
      
      return await this.httpClientService.post(
        url,
        body
      );
    } catch (error) {
      console.error('Error fetching data:', error);
      return axios('fail');
    }
  }

  /**
   * Fetches from endpoint: api/v2/tvd/product and api/v2/tvd/location
   * @returns TVD Details Metrics data
   */
  async getTVDDetailsMetrics(
    groupBy : string,
    metricName : string,
    programName : string,
    costAssumptionsMetrics?: MetricCostAssumptions,
    brandGroup?: availableBrandGroups,
    brands?: string[],
    locationAdvancedFilter?: TvdLocationAdvancedFilter,
    productAdvancedFilter?: TvdProductAdvancedFilter) {
    try {
      if (this.filters == undefined)
        throw new Error('filters are undefined');

      const body = {
        contractModel :this.filters,
        oneCustomerName: this.oneCustomerName,
        costAssumptionsMetrics: costAssumptionsMetrics,
        oneCustomerFilters: this.advancedOneCustomerFilters,
        brandGroup: brandGroup,
        brands: brands,
        currencyCode: this.currencyUomService.getCurrency(),
        unitOfMeasure: this.currencyUomService.getUom(),
        locationAdvancedFilter: locationAdvancedFilter,
        productAdvancedFilter: productAdvancedFilter
      };

      let url = `${this.ENDPOINT_BASE_PATH}/` +
          groupBy +
          `/` +
          metricName +
          `?region=${this.filters.regions}&countries=${this.filters.countries}&period=${this.filters.period}&program=${programName}`
      url = this.filtersService.appendCustomDateRangeToUrl(url);

      return await this.httpClientService.post(
        url,
        body
      );
    } catch (error) {
      console.error('Error fetching data:', error);
      return axios('fail');
    }
  }

  async getTVDAdvancedFilterOptions(
    metricName: string,
    programName: string,
    brandGroup?: availableBrandGroups,
    brands?: string[]) {
    try {
      if (this.filters == undefined)
        throw new Error('filters are undefined');

      const body = {
        contractModel: this.filters,
        oneCustomerName: this.oneCustomerName,
        oneCustomerFilters: this.advancedOneCustomerFilters,
        brandGroup: brandGroup,
        brands: brands
      };

      let url = `${this.ENDPOINT_BASE_PATH}/filters/${metricName}?region=${this.filters.regions}&countries=${this.filters.countries}&period=${this.filters.period}&program=${programName}`;
      url = this.filtersService.appendCustomDateRangeToUrl(url);

      return await this.httpClientService.post(url, body);
    } catch (error) {
      console.error('Error fetching TVD filter options:', error);
      return axios('fail');
    }
  }

  /**
   * Fetches from endpoint: api/v2/tvd/service-location/{metricName}
   * @returns (Pest) TVD Services Details Metrics data
   */
  async getTVDServicesDetailsMetrics(groupBy : string, metricName : string, costAssumptionsMetrics?: MetricCostAssumptions) {
    try {
      if (this.filters == undefined)
        throw new Error('filters are undefined');

      const body = {
        contractModel :this.filters,
        costAssumptionsMetrics: costAssumptionsMetrics,
        oneCustomerName: this.oneCustomerName,
        oneCustomerFilters: this.advancedOneCustomerFilters,
        currencyCode: this.currencyUomService.getCurrency(),
        unitOfMeasure: this.currencyUomService.getUom()
      };

      let url = `${this.ENDPOINT_BASE_PATH}/` +
          `services-${groupBy}` +
          `/` +
          metricName +
          `?region=${this.filters.regions}&countries=${this.filters.countries}&period=${this.filters.period}`;
      url = this.filtersService.appendCustomDateRangeToUrl(url);
      return await this.httpClientService.post(
        url,
        body
      );
    } catch (error) {
      console.error('Error fetching data:', error);
      return axios('fail');
    }
  }


  async getDefaultTVDRates(division:string, regions:string[], desiredCurrencyCode?: string, desiredUnitOfMeasure?: UomOption) {
    if (division?.trim().length > 0 && regions?.length > 0) {
      if (this.filters == undefined)
        throw new Error('filters are undefined');
      const body = {
        contractModel : this.filters,
        oneCustomerName: this.oneCustomerName,
        oneCustomerFilters: this.advancedOneCustomerFilters,
        currencyCode: this.filters.division.toLowerCase() === 'pest' ? undefined : desiredCurrencyCode,
        unitOfMeasure: this.filters.division.toLowerCase() === 'pest' ? undefined : desiredUnitOfMeasure,
      }

      let url = `${this.ENDPOINT_BASE_PATH}/region/${regions}/default-tvdrates?countries=${this.filters.countries}&period=${this.filters.period}`;
      url = this.filtersService.appendCustomDateRangeToUrl(url);
      return await this.httpClientService.post(
        url,
        body
      );
    } else {
      throw new Error('Division and region must not be empty or undefined');
    }
  }

  /**
   * Fetches from endddpoint: api/v2/tvd/program
   * @returns TVD Program Table data
   */
  async getTotalValueDeliveredForPrograms() {
    if (this.filters == undefined) {
      throw new Error('filters are undefined');
    }

    const body = {
      contractModel: this.filters,
      oneCustomerName: this.oneCustomerName,
      oneCustomerFilters: this.advancedOneCustomerFilters,
      currencyCode: this.currencyUomService.getCurrency(),
      unitOfMeasure: this.currencyUomService.getUom(),
    };

    let url = `${this.ENDPOINT_BASE_PATH}/programs?region=${this.filters.regions}&countries=${this.filters.countries}&period=${this.filters.period}`;
    url = this.filtersService.appendCustomDateRangeToUrl(url);

    return await this.httpClientService.post(url, body);
  }

  /**
   * Fetches from endpoint: api/v2/tvd/program/{programName}
   * @returns TVD Program Details data
   */
  async getTotalValueDeliveredForProgramDetails(programName: string) {
    if (this.filters == undefined) {
      throw new Error('filters are undefined');
    }

    if (!programName || programName.trim().length === 0) {
      throw new Error('programName must not be empty or undefined');
    }

    const body = {
      contractModel: this.filters,
      oneCustomerName: this.oneCustomerName,
      oneCustomerFilters: this.advancedOneCustomerFilters,
      currencyCode: this.currencyUomService.getCurrency(),
      unitOfMeasure: this.currencyUomService.getUom(),
    };

    let url = `${this.ENDPOINT_BASE_PATH}/programs/${programName}?region=${this.filters.regions}&countries=${this.filters.countries}&period=${this.filters.period}`;
    url = this.filtersService.appendCustomDateRangeToUrl(url);

    return await this.httpClientService.post(url, body);
  }
}
